import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, ArrowDown } from "lucide-react";
import { Line, EASE } from "./Reveal";

const HERO_BG =
  "https://images.unsplash.com/photo-1633241394397-927cc4ec0845?crop=entropy&cs=srgb&fm=jpg&q=85";
const MAPS_URL =
  "https://www.google.com/maps/search/?api=1&query=Revathy+Stores%2C+61+B%2F5%2C+A.V.+Church+Road%2C+Vannandurai%2C+Besant+Nagar%2C+Chennai+600090";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero"
      className="relative flex min-h-screen items-center justify-center overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0" aria-hidden="true">
        <img
          src={HERO_BG}
          alt=""
          className="absolute inset-0 h-full w-full scale-110 object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.12),transparent_55%)]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 px-5 text-center">
        <Line delay={0.35}>
          <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-brand-brass sm:text-xs">
            Revathy Stores — Kitchen Utensils · Besant Nagar, Chennai
          </span>
        </Line>

        <h1
          data-testid="hero-title"
          className="mt-6 font-display text-[clamp(2.9rem,8vw,5.5rem)] leading-[0.98] tracking-tight"
        >
          <Line delay={0.5}>Everything your</Line>
          <Line delay={0.65}>
            <em className="italic text-brand-brass">kitchen</em> needs.
          </Line>
        </h1>

        <Line delay={0.85}>
          <p className="mx-auto mt-7 max-w-xl text-sm leading-relaxed text-stone-300 sm:text-base">
            Tawas and pressure cookers, steel davaras and tiffin carriers,
            knives that hold their edge, and brass for the pooja room —
            all under one roof.
          </p>
        </Line>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 1.1 }}
          className="mt-10 flex flex-wrap items-center justify-center gap-4"
        >
          <a
            href={MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-directions-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-brand-brass px-7 py-3.5 text-xs font-bold uppercase tracking-[0.16em] text-ink transition-all duration-300 hover:bg-brand-brass-hover hover:shadow-[0_0_36px_rgba(212,175,55,0.4)]"
          >
            <MapPin className="h-4 w-4" />
            Get Directions
          </a>
          <a
            href="#collection"
            data-testid="hero-shelves-btn"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-xs font-bold uppercase tracking-[0.16em] text-stone-100 transition-all duration-300 hover:border-brand-brass hover:text-brand-brass"
          >
            Browse the shelves
            <ArrowDown className="h-4 w-4" />
          </a>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2">
        <span className="block font-mono text-[10px] uppercase tracking-[0.3em] text-stone-500">
          Scroll
        </span>
        <span className="animate-scroll-cue mx-auto mt-2 block h-10 w-px bg-brand-brass/70" />
      </div>

      <div className="absolute bottom-10 right-10 z-10 hidden lg:block">
        <div className="relative h-32 w-32">
          <svg viewBox="0 0 100 100" className="animate-spin-slow h-full w-full">
            <defs>
              <path id="circlePath" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
            </defs>
            <text className="fill-stone-400 font-mono text-[8.2px] uppercase tracking-[0.24em]">
              <textPath href="#circlePath">
                kitchen utensils · besant nagar · chennai ·
              </textPath>
            </text>
          </svg>
          <ArrowDown className="absolute inset-0 m-auto h-5 w-5 text-brand-brass" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
